import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  SimpleButton,
  LoadingButton,
  FormDialog,
  Input,
  TextArea,
  Select,
  useStateContext,
  Toast,
} from "@veda/dashboard-ui";
import { BsBoxArrowUpRight } from "react-icons/bs";
import { MdAdd } from "react-icons/md";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";
import { usePppAiContext } from "../hooks/usePppAiContext";

const STATO_CFG = {
  bozza:      { key: "ppp_prog_stato_bozza",      cls: "bg-gray-100 text-gray-700" },
  approvato:  { key: "ppp_prog_stato_approvato",  cls: "bg-blue-100 text-blue-700" },
  pubblicato: { key: "ppp_prog_stato_pubblicato", cls: "bg-green-100 text-green-700" },
  archiviato: { key: "ppp_prog_stato_archiviato", cls: "bg-gray-200 text-gray-500" },
};

function StatoBadge({ stato, _locate }) {
  const cfg = STATO_CFG[stato] ?? { key: stato, cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      {_locate(cfg.key) ?? stato}
    </span>
  );
}

function formatEuro(val) {
  if (!val) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(val));
}

export default function ProgrammazioneList() {
  const navigate = useNavigate();
  const { _locate, setOpenForm, addToast } = useStateContext();
  const { can, user, ready } = useAuthUser();
  usePppAiContext('ppp.programmazione');
  const api = usePppApi();

  const RUOLI_PA = ['dirigente', 'ufficio_tecnico', 'ufficio_amministrativo', 'amministratore_ente'];
  const isRup    = user?.role === 'rup';
  const isPaEnte = RUOLI_PA.includes(user?.role);

  const [programmi, setProgrammi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ enteId: "", enteConcedente: "", annoRiferimento: new Date().getFullYear(), note: "" });
  const [entiRup, setEntiRup] = useState([]);
  const [mioEnte, setMioEnte] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    api.listProgrammi({
      onSuccess: (data) => { setProgrammi(data); setLoading(false); },
      onError: () => setLoading(false),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!ready) return;
    if (isRup) {
      api.listMieiEnti({
        onSuccess: (data) => setEntiRup(data.filter((e) => e.stato === 'accettato')),
        onError: () => {},
      });
    } else if (isPaEnte) {
      api.getMioEnte({
        onSuccess: (data) => {
          setMioEnte(data);
          setForm((f) => ({ ...f, enteConcedente: data.denominazione }));
        },
        onError: () => {},
      });
    }
  }, [ready, isRup, isPaEnte]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCrea = () => {
    setSaving(true);
    const payload = {
      enteConcedente:  form.enteConcedente,
      annoRiferimento: form.annoRiferimento,
      note:            form.note,
      ...(isRup && form.enteId ? { enteId: parseInt(form.enteId, 10) } : {}),
    };
    api.creaProgramma(payload, {
      onSuccess: (data) => {
        setSaving(false);
        setOpenForm(false);
        refresh();
        navigate(`/ppp/programmazione/${data.id}`);
      },
      onFormError: () => setSaving(false),
      onError: () => setSaving(false),
    });
  };

  const cols = [
    { name: "action",    label: "",                                          size: "w-10 max-w-[40px]", orderable: 0 },
    { name: "triennio",  label: _locate("ppp_programmazione_col_triennio") },
    { name: "stato",     label: _locate("ppp_programmazione_col_stato"),    size: "w-28" },
    { name: "interventi",label: _locate("ppp_programmazione_col_interventi"),size: "w-24" },
    { name: "importo",   label: _locate("ppp_programmazione_col_importo"),  size: "w-36" },
    { name: "delibera",  label: _locate("ppp_programmazione_col_delibera"), size: "w-40" },
  ];

  const cells_style = {
    action: (row) => (
      <button
        onClick={() => navigate(`/ppp/programmazione/${row.id}`)}
        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
        title={_locate("ppp_apri")}
      >
        <BsBoxArrowUpRight size={14} />
      </button>
    ),
    triennio: (row) => (
      <div className="cursor-pointer" onClick={() => navigate(`/ppp/programmazione/${row.id}`)}>
        <div className="text-xs font-bold text-[#555454] dark:text-dark-text-normal hover:text-gray-400">
          {row.annoRiferimento}–{row.annoRiferimento + 2}
        </div>
        <div className="text-xs text-[#828c95] mt-0.5">
          {row.enteConcedente}
        </div>
      </div>
    ),
    stato:      (row) => <StatoBadge stato={row.stato} _locate={_locate} />,
    interventi: (row) => <span className="text-xs text-center block">{row.totaleInterventi}</span>,
    importo:    (row) => <span className="text-xs font-mono">{formatEuro(row.totaleImporto)}</span>,
    delibera:   (row) => (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {row.numeroDelibera
          ? `n. ${row.numeroDelibera}${row.dataApprovazione ? ` del ${new Date(row.dataApprovazione).toLocaleDateString("it-IT")}` : ""}`
          : "—"}
      </span>
    ),
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {_locate("ppp_programmazione_lista_titolo")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {_locate("ppp_programmazione_lista_sottotitolo")}
          </p>
        </div>
        {can('ppp.programmazione.create') && (
          <SimpleButton customFunc={() => setOpenForm(true)} text={<span className="flex items-center gap-1"><MdAdd size={16} />{_locate("ppp_programmazione_nuovo")}</span>} />
        )}
      </div>

      <div className="mt-6">
        <Table
          id="ppp-programmazione"
          dataset={programmi}
          cols={cols}
          cells_style={cells_style}
          loading={loading}
        />
      </div>
      </div>

      <FormDialog title={_locate("ppp_prog_nuovo_titolo")} size="w-[520px]">
        <div className="space-y-5">
          <p className="text-sm text-gray-500">{_locate("ppp_prog_form_descrizione")}</p>
          {isRup ? (
            <Select
              label={_locate("ppp_prog_form_ente") || "Ente concedente"}
              required
              value={form.enteId}
              onChange={(e) => {
                const val = e.target.value;
                const selected = entiRup.find((en) => String(en.enteId) === val);
                setForm((f) => ({
                  ...f,
                  enteId: val,
                  enteConcedente: selected?.denominazione ?? f.enteConcedente,
                }));
              }}
              options={entiRup.map((en) => ({ value: String(en.enteId), label: en.denominazione + (en.comune ? ` (${en.comune})` : "") }))}
            />
          ) : isPaEnte ? (
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1">{_locate("ppp_prog_form_ente") || "Ente concedente"}</p>
              <p className="text-sm font-medium text-gray-800 dark:text-dark-text-normal">
                {mioEnte?.denominazione ?? "—"}
              </p>
            </div>
          ) : (
            <Input
              label={_locate("ppp_prog_form_ente")}
              required
              value={form.enteConcedente}
              onChange={(e) => setForm((f) => ({ ...f, enteConcedente: e.target.value }))}
            />
          )}
          <Input
            label={_locate("ppp_prog_form_anno")}
            required
            type="number"
            value={form.annoRiferimento}
            onChange={(e) => setForm((f) => ({ ...f, annoRiferimento: parseInt(e.target.value) || f.annoRiferimento }))}
          />
          <TextArea
            label={_locate("ppp_prog_form_note")}
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            rows={3}
          />
          <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
            <SimpleButton customFunc={() => setOpenForm(false)} text={_locate("ppp_prog_form_annulla")} />
            <LoadingButton
              isLoading={saving}
              customFunc={form.enteConcedente && form.annoRiferimento && (!isRup || form.enteId) ? handleCrea : undefined}
              text={_locate("ppp_prog_form_crea")}
            />
          </div>
        </div>
      </FormDialog>
    </div>
  );
}
